import {
  IAdjustmentResponse,
  IBidPricingResponse,
  IDrawPricingContract,
  IDropResponse,
  INetTotalResponse,
} from "@miplatform/types";
import { getCurrencyString } from "@miplatform/utils";

export const COLUMN_WIDTH = 88;

export interface IDrawRow {
  total: number;
  draws: IDrawPricingContract[];
}

export const getDrawValue = (row: IDrawRow, column: string) =>
  row.draws?.find((draw) => draw.draw === column)?.value;

export const EMPTY_DRAW_ROW: IDrawRow = { total: 0, draws: [] };

export const getColumns = (priceDetails?: IBidPricingResponse | null) =>
  priceDetails?.draws.map((draw) => draw.draw) ?? [];

export const formatPrice = (value: number | undefined, isRemoved?: boolean) => {
  if (!value) return "$0.00";
  const formatted = getCurrencyString(Math.abs(value));
  return isRemoved || value < 0 ? `(${formatted})` : formatted;
};

export const groupDrops = (
  drops: IDropWithStatus[],
  separateByRemovalStatus = false
) => {
  const groups = new Map<string, IGroupedDrop>();

  const chargedDrops = new Map<string, Set<string>>();

  drops.forEach((drop) => {
    const isRemoved = drop.isRemoved ?? drop.price < 0;
    const key = separateByRemovalStatus
      ? `${drop.dropTypeId}-${isRemoved ? "removed" : "added"}`
      : drop.dropTypeId;
    const chargeKey = `${drop.floorId}-${drop.categoryId}`;
    const chargeKeys = chargedDrops.get(key) ?? new Set<string>();
    const isFirstCharge = !chargeKeys.has(chargeKey);
    chargeKeys.add(chargeKey);
    chargedDrops.set(key, chargeKeys);
    const existing = groups.get(key);

    if (existing) {
      existing.count += 1;
      if (isFirstCharge) {
        existing.total += drop.price;
        drop.draws?.forEach((drawValue) => {
          const draw = existing.draws.find(
            (existingDraw) => existingDraw.draw === drawValue.draw
          );
          if (draw) draw.value += drawValue.value;
          else existing.draws.push({ ...drawValue });
        });
      }
      if (drop.netTotal) {
        if (existing.netTotal && isFirstCharge) {
          existing.netTotal.total += drop.netTotal.total;
          drop.netTotal.draws?.forEach((drawValue) => {
            const draw = existing.netTotal?.draws.find(
              (existingDraw) => existingDraw.draw === drawValue.draw
            );
            if (draw) draw.value += drawValue.value;
            else existing.netTotal?.draws.push({ ...drawValue });
          });
        } else if (!existing.netTotal) {
          existing.netTotal = {
            total: drop.netTotal.total,
            draws: drop.netTotal.draws?.map((draw) => ({ ...draw })) ?? [],
          };
        }
      }
    } else {
      groups.set(key, {
        dropTypeId: drop.dropTypeId,
        dropTypeName: drop.dropTypeName,
        categoryId: drop.categoryId,
        categoryName: drop.categoryName,
        count: 1,
        total: drop.price,
        draws: drop.draws?.map((draw) => ({ ...draw })) ?? [],
        isRemoved,
        adjustment: drop.adjustment,
        adjustments: drop.adjustment ? [drop.adjustment] : [],
        netTotal: drop.netTotal
          ? {
              total: drop.netTotal.total,
              draws: drop.netTotal.draws?.map((d) => ({ ...d })) ?? [],
            }
          : undefined,
      });
    }
  });

  return [...groups.values()];
};

export interface IDropWithStatus extends IDropResponse {
  isRemoved?: boolean;
}

export interface IGroupedDrop {
  dropTypeId: string;
  dropTypeName: string;
  categoryId?: string;
  categoryName?: string;
  count: number;
  total: number;
  draws: IDrawPricingContract[];
  isRemoved: boolean;
  adjustment?: IAdjustmentResponse | null;
  adjustments?: IAdjustmentResponse[];
  netTotal?: INetTotalResponse | null;
}
